/* ===================================================================
   Salted Perfection — Store Locator
   Vanilla JS, no build step. Reads the SaltSync Pro public feed and
   drives a Mapbox GL map + searchable, filterable store list.

   - Multiple section instances on one page are supported.
   - Works without a Mapbox token (list still renders; map shows a
     friendly message).
   - State (search origin, radius, channel filters, selected store) is
     reflected in the URL hash-free query (?store=) for deep links.
   =================================================================== */
(function () {
  "use strict";

  var CHANNEL_LABELS = {
    GROCERY: "Grocery",
    SPECIALTY: "Specialty Food",
    BUTCHER_DELI: "Butcher / Deli",
    HEALTH_NATURAL: "Health & Natural Foods",
    FARM_STAND: "Farm Stand",
    MARKET: "Market",
    WINE_SPIRITS: "Wine & Spirits",
    KITCHEN_HOME: "Kitchen & Home Goods",
    GIFT_SHOP: "Gift Shop",
    HOSPITALITY: "Hospitality",
    OTHER: "Other",
  };
  var FEED_CACHE_TTL = 5 * 60 * 1000; // 5 min in sessionStorage
  var EARTH_MI = 3958.8;

  function $(root, sel) { return root.querySelector(sel); }
  function el(tag, cls) { var n = document.createElement(tag); if (cls) n.className = cls; return n; }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function toRad(d) { return (d * Math.PI) / 180; }
  function haversineMi(a, b) {
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return EARTH_MI * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }
  function fmtDist(mi) {
    if (mi == null) return "";
    if (mi < 10) return mi.toFixed(1) + " mi";
    return Math.round(mi) + " mi";
  }
  function addrLines(a) {
    if (!a) return [];
    var l1 = [a.line1, a.line2].filter(Boolean).join(", ");
    var l2 = [a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return [l1, l2].filter(Boolean);
  }
  function addrOneLine(a) { return addrLines(a).join(", "); }

  // ---- business hours parsing ----------------------------------------------
  // Hours arrive as a human string. The Google-autofilled format is reliably
  // structured ("Mon–Fri: 9:00 AM – 5:00 PM | Sat: 10:00 AM – 2:00 PM | Sun:
  // Closed"), so we parse that into per-weekday periods to drive a live
  // "Open now" badge. Free-text hours a human typed ("Mon-Fri 9-5", no colon)
  // won't match — parseHours returns null and the card just shows the raw
  // string with no badge. Graceful degradation, never a wrong claim from a
  // string we couldn't confidently read.
  var DAY_IDX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  var DAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  function dayKey(s) { return String(s || "").slice(0, 3).toLowerCase(); }
  function fmtMin(m) {
    var h = Math.floor(m / 60), mm = m % 60, ap = h < 12 ? "AM" : "PM", hh = h % 12;
    if (hh === 0) hh = 12;
    return hh + (mm ? ":" + (mm < 10 ? "0" : "") + mm : "") + " " + ap;
  }
  function parseTimeToMin(t) {
    t = String(t || "").trim().toLowerCase().replace(/\./g, "");
    var m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
    if (!m) return null;
    var h = parseInt(m[1], 10), min = m[2] ? parseInt(m[2], 10) : 0;
    if (m[3]) { if (h === 12) h = 0; if (m[3] === "pm") h += 12; }
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }
  // Normalize the messy spaces/dashes Google emits (U+202F narrow no-break,
  // U+2009 thin, U+00A0 nbsp; en/em dashes) down to plain ASCII so one set of
  // regexes handles every real-world string.
  function normHours(s) {
    return String(s || "")
      .replace(/[      ]/g, " ")
      .replace(/[‒–—―]/g, "-")
      .replace(/\s+/g, " ")
      .trim();
  }
  function expandDayToken(tok) {
    tok = tok.trim().toLowerCase();
    if (tok === "daily" || tok === "everyday") return [0, 1, 2, 3, 4, 5, 6];
    if (tok === "weekday" || tok === "weekdays") return [1, 2, 3, 4, 5];
    if (tok === "weekend" || tok === "weekends") return [0, 6];
    var parts = tok.split(/\s*-\s*/);
    var a = DAY_IDX[dayKey(parts[0])];
    if (a == null) return null;
    if (parts.length === 1) return [a];
    var b = DAY_IDX[dayKey(parts[1])];
    if (b == null) return null;
    var out = [], d = a;
    for (var g = 0; g < 7; g++) { out.push(d); if (d === b) break; d = (d + 1) % 7; }
    return out;
  }
  function parseDayValue(v) {
    var lo = String(v || "").trim().toLowerCase();
    if (!lo) return null;
    if (/closed/.test(lo)) return "closed";
    if (/24\s*hours|open 24|24\/7|24 hrs|all day/.test(lo)) return "24";
    var rm = lo.split(/\s*(?:-|to|–|—|until|till)\s*/);
    if (rm.length < 2) return null;
    var o = parseTimeToMin(rm[0]), c = parseTimeToMin(rm[rm.length - 1]);
    if (o === null || c === null) return null;
    return [{ o: o, c: c }];
  }
  // Accepts both the Google-autofilled string ("Mon-Fri 9:00 AM – 5:00 PM,
  // Sat 8:00 AM – 4:00 PM, Sun Closed", "Daily 7 AM – 9 PM") and the older
  // pipe/colon format. Segments split on , ; |. A segment may lead with a day
  // token (then a value) or be a dayless value ("Open 24 hours" -> whole week)
  // or a continuation of the previous day's hours (split shifts). Returns null
  // if any segment's value can't be read, so we never show a wrong badge.
  function parseHours(str) {
    var s = normHours(str);
    if (!s) return null;
    var segs = s.split(/\s*[|,;]\s*/);
    var byDay = {}, any = false, currentDays = null;
    for (var i = 0; i < segs.length; i++) {
      var seg = segs[i].trim();
      if (!seg) continue;
      var low = seg.toLowerCase();
      var m = low.match(/^([a-z]{3,9}(?:\s*-\s*[a-z]{3,9})?)\s*:?\s+(.+)$/);
      var days = null, valStr = null;
      if (m) { days = expandDayToken(m[1]); if (days) valStr = m[2]; }
      if (!days) { days = currentDays || [0, 1, 2, 3, 4, 5, 6]; valStr = low; }
      var val = parseDayValue(valStr);
      if (val === null) return null;
      for (var j = 0; j < days.length; j++) {
        var d = days[j];
        if (val === "closed" || val === "24") byDay[d] = val;
        else if (Array.isArray(byDay[d])) byDay[d] = byDay[d].concat(val);
        else byDay[d] = val.slice();
        any = true;
      }
      currentDays = days;
    }
    return any ? { byDay: byDay } : null;
  }
  // Current weekday + minutes-since-midnight in a given IANA timezone, via
  // Intl (DST-correct). Returns null if the tz is missing/invalid so callers
  // can fall back to the visitor's clock.
  function nowPartsInTz(tz) {
    if (!tz) return null;
    try {
      var parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(new Date());
      var wd, hh, mm;
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === "weekday") wd = parts[i].value;
        else if (parts[i].type === "hour") hh = parts[i].value;
        else if (parts[i].type === "minute") mm = parts[i].value;
      }
      var DAYMAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      var day = DAYMAP[wd], hour = parseInt(hh, 10) % 24, min = parseInt(mm, 10);
      if (day == null || isNaN(hour) || isNaN(min)) return null;
      return { day: day, mins: hour * 60 + min };
    } catch (e) { return null; }
  }
  // Returns { open, tone: 'open'|'soon'|'closed', label } or null. Computes
  // "now" in the store's timezone when the feed provides one (national rollout)
  // and falls back to the visitor's local clock otherwise.
  function hoursStatus(parsed, tz) {
    if (!parsed) return null;
    var np = nowPartsInTz(tz);
    var day, mins;
    if (np) { day = np.day; mins = np.mins; }
    else { var now = new Date(); day = now.getDay(); mins = now.getHours() * 60 + now.getMinutes(); }
    var today = parsed.byDay[day];
    if (today === "24") return { open: true, tone: "open", label: "Open 24 hours" };
    if (Array.isArray(today)) {
      for (var i = 0; i < today.length; i++) {
        var p = today[i];
        var isOpen = p.c > p.o ? (mins >= p.o && mins < p.c) : (mins >= p.o || mins < p.c);
        if (isOpen) {
          var soon = p.c > mins && (p.c - mins) <= 60;
          return { open: true, tone: soon ? "soon" : "open", label: soon ? "Closes " + fmtMin(p.c) : "Open now" };
        }
      }
      var next = null;
      for (var k = 0; k < today.length; k++) { if (today[k].o > mins && (next === null || today[k].o < next)) next = today[k].o; }
      if (next !== null) return { open: false, tone: "closed", label: "Closed · Opens " + fmtMin(next) };
    }
    for (var d = 1; d <= 7; d++) {
      var nd = (day + d) % 7, v = parsed.byDay[nd];
      if (v === "24") return { open: false, tone: "closed", label: "Closed · Opens " + (d === 1 ? "tomorrow" : DAY_FULL[nd]) };
      if (Array.isArray(v) && v.length) {
        return { open: false, tone: "closed", label: "Closed · Opens " + (d === 1 ? "tomorrow" : DAY_FULL[nd]) + " " + fmtMin(v[0].o) };
      }
    }
    return { open: false, tone: "closed", label: "Closed" };
  }

  // Wait until window.mapboxgl is available (loaded with defer from CDN).
  function whenMapbox(cb, timeoutMs) {
    var start = Date.now();
    (function poll() {
      if (window.mapboxgl) return cb(window.mapboxgl);
      if (Date.now() - start > (timeoutMs || 8000)) return cb(null);
      setTimeout(poll, 60);
    })();
  }

  // Be forgiving about which Mapbox URL the merchant pastes. Mapbox GL needs
  // the "mapbox://styles/USER/STYLEID" Style URL, but Studio also shows a
  // share/preview URL ("https://api.mapbox.com/styles/v1/USER/STYLEID.html?…")
  // and an editor URL ("https://studio.mapbox.com/styles/USER/STYLEID/edit/…").
  // Pull USER/STYLEID out of any of them so a custom style "just works".
  function normMapStyle(s) {
    s = String(s || "").trim();
    if (!s) return "mapbox://styles/mapbox/light-v11";
    if (/^mapbox:\/\/styles\//i.test(s)) return s;
    var m = s.match(/styles\/(?:v1\/)?([^\/\s]+)\/([^\/?.#\s]+)/i);
    if (m) return "mapbox://styles/" + m[1] + "/" + m[2];
    return s;
  }

  function readConfig(root) {
    var node = $(root, "[data-ssp-config]");
    var cfg = {};
    try { cfg = JSON.parse(node.textContent); } catch (e) { cfg = {}; }
    cfg.token = (cfg.token || "").trim();
    cfg.feedUrl = cfg.feedUrl || "https://saltsyncpro.com/api/public/store-locator.json";
    cfg.mapStyle = normMapStyle(cfg.mapStyle);
    cfg.center = Array.isArray(cfg.center) && cfg.center.length === 2 ? cfg.center : [-98.5795, 39.8283];
    cfg.zoom = typeof cfg.zoom === "number" ? cfg.zoom : 3.4;
    cfg.markerColor = cfg.markerColor || "#D49312";
    cfg.country = (cfg.country || "us").trim();
    // Home region for the default list sort when the viewer's location can't
    // be resolved. Defaults to mid-Michigan (Salted Perfection's home turf).
    cfg.homeLat = typeof cfg.homeLat === "number" ? cfg.homeLat : 44.0;
    cfg.homeLng = typeof cfg.homeLng === "number" ? cfg.homeLng : -85.5;
    return cfg;
  }

  function fetchFeed(url) {
    var key = "ssp_locator_feed:" + url;
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && cached.t && Date.now() - cached.t < FEED_CACHE_TTL) {
          return Promise.resolve(cached.d);
        }
      }
    } catch (e) {}
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) {
        if (!r.ok) throw new Error("Feed HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data })); } catch (e) {}
        return data;
      });
  }

  function geojson(stores) {
    return {
      type: "FeatureCollection",
      features: stores.map(function (s) {
        return {
          type: "Feature",
          id: s.id,
          geometry: { type: "Point", coordinates: [s.lng, s.lat] },
          properties: { id: s.id, name: s.name },
        };
      }),
    };
  }

  function Locator(root) {
    var cfg = readConfig(root);
    var listEl = $(root, "[data-ssp-list]");
    var countEl = $(root, "[data-ssp-count]");
    var emptyEl = $(root, "[data-ssp-empty]");
    var emptyResetBtn = $(root, "[data-ssp-empty-reset]");
    var channelSelectEl = $(root, "[data-ssp-channel-select]");
    var channelWrapEl = $(root, "[data-ssp-channel-wrap]");
    var productGroupEl = $(root, "[data-ssp-product-group]");
    var productChipsEl = $(root, "[data-ssp-product-chips]");
    var searchForm = $(root, "[data-ssp-searchform]");
    var searchInput = $(root, "[data-ssp-search]");
    var clearBtn = $(root, "[data-ssp-clear]");
    var nearBtn = $(root, "[data-ssp-nearme]");
    var radiusWrap = $(root, "[data-ssp-radius-wrap]");
    var radiusSel = $(root, "[data-ssp-radius]");
    var mapEl = $(root, "[data-ssp-map]");
    var mapMsg = $(root, "[data-ssp-mapmsg]");

    var state = {
      all: [],          // all stores from feed
      visible: [],      // after channel + radius filtering, sorted
      channels: {},     // selected channel set ({} = all)
      lines: {},        // selected product-line set ({} = all)
      origin: null,     // {lat,lng} explicit search/near-me origin
      // Implicit default-sort origin: brand home region now, upgraded to the
      // viewer's approximate location once /geo resolves. Sorts the list only.
      sortOrigin: (typeof cfg.homeLat === "number" && typeof cfg.homeLng === "number")
        ? { lat: cfg.homeLat, lng: cfg.homeLng } : null,
      radius: parseInt(radiusSel.value, 10) || 25,
      selectedId: null,
      map: null,
      mapReady: false,
      popup: null,
      cardById: {},
    };

    // ---- skeleton on load
    renderSkeleton();

    fetchFeed(cfg.feedUrl)
      .then(function (data) {
        state.all = (data && Array.isArray(data.stores) ? data.stores : []).filter(function (s) {
          return typeof s.lat === "number" && typeof s.lng === "number";
        });
        buildChannelSelect();
        buildLineChips();
        applyFilters();
        initMap();
        applyDeepLink();
        resolveDefaultSortOrigin();
      })
      .catch(function (err) {
        listEl.innerHTML = "";
        countEl.textContent = "";
        emptyEl.hidden = false;
        $(emptyEl, ".ssp-locator__empty-title").textContent = "Couldn't load stores";
        $(emptyEl, ".ssp-locator__empty-body").textContent = "Please try again in a moment.";
        showMapMsg("Store data is temporarily unavailable.");
        if (window.console) console.error("[ssp-locator]", err);
      });

    // ---- channels ----
    function presentChannels() {
      var seen = {};
      state.all.forEach(function (s) { if (s.channel) seen[s.channel] = true; });
      // Preserve a sensible display order.
      return Object.keys(CHANNEL_LABELS).filter(function (k) { return seen[k]; });
    }
    // Store type is a secondary, single-select filter — a quiet dropdown next
    // to the search box rather than a second row of pills competing with the
    // primary product-line chips.
    function buildChannelSelect() {
      if (!channelSelectEl) return;
      var chans = presentChannels();
      if (channelWrapEl) channelWrapEl.hidden = chans.length === 0;
      if (!chans.length) return;
      channelSelectEl.innerHTML = "";
      var all = el("option");
      all.value = "";
      all.textContent = "All store types";
      channelSelectEl.appendChild(all);
      chans.forEach(function (k) {
        var o = el("option");
        o.value = k;
        o.textContent = CHANNEL_LABELS[k];
        channelSelectEl.appendChild(o);
      });
      channelSelectEl.addEventListener("change", function () {
        var v = channelSelectEl.value;
        state.channels = v ? { [v]: true } : {};
        applyFilters();
        refreshMapData();
      });
    }

    // ---- product lines ----
    function presentLines() {
      var seen = {};
      state.all.forEach(function (s) {
        (s.product_lines || []).forEach(function (name) { if (name) seen[name] = true; });
      });
      return Object.keys(seen).sort();
    }
    function buildLineChips() {
      if (!productChipsEl) return;
      var lines = presentLines();
      productChipsEl.innerHTML = "";
      if (productGroupEl) productGroupEl.hidden = lines.length === 0;
      if (!lines.length) return;
      productChipsEl.appendChild(makeLineChip("All", "__all"));
      lines.forEach(function (name) { productChipsEl.appendChild(makeLineChip(name, name)); });
      syncLineChips();
    }
    function makeLineChip(label, key) {
      var b = el("button", "ssp-locator__chip");
      b.type = "button";
      b.textContent = label;
      b.setAttribute("data-line", key);
      b.setAttribute("aria-pressed", "false");
      b.addEventListener("click", function () {
        if (key === "__all") {
          state.lines = {};
        } else if (state.lines[key]) {
          delete state.lines[key];
        } else {
          state.lines[key] = true;
        }
        syncLineChips();
        applyFilters();
        refreshMapData();
      });
      return b;
    }
    function syncLineChips() {
      if (!productChipsEl) return;
      var anySel = Object.keys(state.lines).length > 0;
      Array.prototype.forEach.call(productChipsEl.children, function (c) {
        var k = c.getAttribute("data-line");
        var on = k === "__all" ? !anySel : !!state.lines[k];
        c.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }

    // ---- filtering + sorting ----
    function applyFilters() {
      var anyChan = Object.keys(state.channels).length > 0;
      var anyLine = Object.keys(state.lines).length > 0;
      var list = state.all.filter(function (s) {
        if (anyChan && !state.channels[s.channel]) return false;
        if (anyLine) {
          var lines = s.product_lines || [];
          var hit = false;
          for (var i = 0; i < lines.length; i++) { if (state.lines[lines[i]]) { hit = true; break; } }
          if (!hit) return false;
        }
        return true;
      });
      if (state.origin) {
        // Explicit search / "Near me": show distances + filter by radius.
        list.forEach(function (s) {
          s._dist = haversineMi(state.origin, { lat: s.lat, lng: s.lng });
        });
        if (state.radius > 0) {
          list = list.filter(function (s) { return s._dist <= state.radius; });
        }
        list.sort(function (a, b) { return a._dist - b._dist; });
      } else if (state.sortOrigin) {
        // Implicit default ordering: nearest to the viewer first (or the
        // brand's home region as a fallback). No distance shown, no radius
        // filter, and the map stays national — this only reorders the list.
        list.forEach(function (s) {
          s._dist = null;
          s._sortDist = haversineMi(state.sortOrigin, { lat: s.lat, lng: s.lng });
        });
        list.sort(function (a, b) { return a._sortDist - b._sortDist; });
      } else {
        list.forEach(function (s) { s._dist = null; });
        // Feed already arrives sorted by state/city/name.
      }
      state.visible = list;
      renderList();
    }

    // Ask the worker for the viewer's approximate location (Cloudflare edge
    // geo — no permission prompt) and re-sort the list nearest-first. Until/
    // unless this resolves, the list stays sorted by the brand home region.
    // Never touches the map (stays national) and yields to explicit searches.
    function resolveDefaultSortOrigin() {
      var geoUrl = String(cfg.feedUrl || "").replace(/store-locator\.json.*$/, "geo.json");
      if (!/geo\.json$/.test(geoUrl)) return;
      fetch(geoUrl, { headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (g) {
          if (!g || typeof g.lat !== "number" || typeof g.lng !== "number") return;
          state.sortOrigin = { lat: g.lat, lng: g.lng };
          if (!state.origin) applyFilters(); // re-sort list only; map untouched
        })
        .catch(function () { /* keep the home-region fallback */ });
    }

    // ---- list rendering ----
    function renderSkeleton() {
      listEl.innerHTML = "";
      countEl.textContent = "Loading stores…";
      emptyEl.hidden = true;
      for (var i = 0; i < 5; i++) {
        var li = el("li");
        var sk = el("div", "ssp-skel");
        sk.innerHTML =
          '<div class="ssp-skel__line w-60"></div>' +
          '<div class="ssp-skel__line w-80"></div>' +
          '<div class="ssp-skel__line w-40"></div>';
        li.appendChild(sk);
        listEl.appendChild(li);
      }
    }

    function renderList() {
      listEl.innerHTML = "";
      state.cardById = {};
      var n = state.visible.length;
      if (!n) {
        countEl.textContent = "";
        var filtered = !!state.origin || Object.keys(state.channels).length > 0;
        var titleEl = $(emptyEl, ".ssp-locator__empty-title");
        var bodyEl = $(emptyEl, ".ssp-locator__empty-body");
        var resetEl = $(emptyEl, "[data-ssp-empty-reset]");
        if (titleEl) titleEl.textContent = filtered ? "No stores match your filters" : "No stores found";
        if (bodyEl) bodyEl.textContent = filtered
          ? "Try a wider radius, a different search, or reset to see every store."
          : "Check back soon — we're adding new retail partners.";
        if (resetEl) resetEl.hidden = !filtered;
        emptyEl.hidden = false;
        return;
      }
      emptyEl.hidden = true;
      countEl.textContent = n + (n === 1 ? " store" : " stores") +
        (state.origin && state.radius > 0 ? " within " + state.radius + " mi" : "");

      var frag = document.createDocumentFragment();
      state.visible.forEach(function (s) {
        var li = el("li");
        var card = el("article", "ssp-card");
        card.tabIndex = 0;
        card.setAttribute("role", "button");
        card.setAttribute("aria-label", s.name + (s._dist != null ? ", " + fmtDist(s._dist) + " away" : ""));
        card.setAttribute("data-id", s.id);

        var top = el("div", "ssp-card__top");
        var name = el("h3", "ssp-card__name");
        name.textContent = s.name;
        top.appendChild(name);
        if (s._dist != null) {
          var dist = el("span", "ssp-card__dist");
          dist.textContent = fmtDist(s._dist);
          top.appendChild(dist);
        }
        card.appendChild(top);

        // Live open/closed status, only when hours parse confidently. Computed
        // in the store's timezone (s.tz) when available.
        var status = hoursStatus(parseHours(s.hours), s.tz);
        var metaRow = el("div", "ssp-card__meta");
        if (status) {
          var stat = el("span", "ssp-card__status ssp-card__status--" + status.tone);
          stat.textContent = status.label;
          metaRow.appendChild(stat);
        }
        if (s.channel && CHANNEL_LABELS[s.channel]) {
          var ch = el("span", "ssp-card__channel");
          ch.textContent = CHANNEL_LABELS[s.channel];
          metaRow.appendChild(ch);
        }
        if (metaRow.childNodes.length) card.appendChild(metaRow);

        var lines = addrLines(s.address);
        if (lines.length) {
          var addr = el("p", "ssp-card__addr");
          addr.innerHTML = lines.map(esc).join("<br>");
          card.appendChild(addr);
        }
        if (s.description) {
          var desc = el("p", "ssp-card__desc");
          desc.textContent = s.description;
          card.appendChild(desc);
        }
        if (s.hours) {
          var hrs = el("p", "ssp-card__hours");
          hrs.textContent = s.hours;
          card.appendChild(hrs);
        }
        if (s.product_lines && s.product_lines.length) {
          var car = el("p", "ssp-card__carries");
          var carLabel = el("span", "ssp-card__carries-label");
          carLabel.textContent = "Carries: ";
          car.appendChild(carLabel);
          car.appendChild(document.createTextNode(s.product_lines.join(" · ")));
          card.appendChild(car);
          // For limited-selection stores, list the specific products too.
          if (s.products && s.products.length) {
            var prods = el("p", "ssp-card__carries-products");
            prods.textContent = s.products.join(", ");
            card.appendChild(prods);
          }
        }

        var actions = el("div", "ssp-card__actions");
        var dirHref = directionsUrl(s);
        var dir = el("a", "ssp-card__btn ssp-card__btn--primary");
        dir.href = dirHref;
        dir.target = "_blank";
        dir.rel = "noopener";
        dir.innerHTML = pinSvg() + "<span>Directions</span>";
        dir.addEventListener("click", function (e) { e.stopPropagation(); });
        actions.appendChild(dir);

        if (s.phone) {
          var tel = el("a", "ssp-card__btn");
          tel.href = "tel:" + String(s.phone).replace(/[^+\d]/g, "");
          tel.innerHTML = phoneSvg() + "<span>" + esc(s.phone) + "</span>";
          tel.addEventListener("click", function (e) { e.stopPropagation(); });
          actions.appendChild(tel);
        }
        if (s.website) {
          var web = el("a", "ssp-card__btn");
          web.href = normUrl(s.website);
          web.target = "_blank";
          web.rel = "noopener";
          web.innerHTML = webSvg() + "<span>Website</span>";
          web.addEventListener("click", function (e) { e.stopPropagation(); });
          actions.appendChild(web);
        }
        var share = el("button", "ssp-card__btn");
        share.type = "button";
        share.innerHTML = shareSvg() + "<span>Share</span>";
        share.addEventListener("click", (function (store, btn) {
          return function (e) { e.stopPropagation(); shareStore(store, btn); };
        })(s, share));
        actions.appendChild(share);
        card.appendChild(actions);

        card.addEventListener("click", function () { selectStore(s.id, { fly: true }); });
        card.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectStore(s.id, { fly: true }); }
        });
        card.addEventListener("mouseenter", function () { highlightPoint(s.id); });
        card.addEventListener("mouseleave", function () { highlightPoint(state.selectedId); });

        li.appendChild(card);
        frag.appendChild(li);
        state.cardById[s.id] = card;
      });
      listEl.appendChild(frag);
      if (state.selectedId) markActiveCard(state.selectedId);
    }

    function directionsUrl(s) {
      var dest = encodeURIComponent(addrOneLine(s.address) || (s.lat + "," + s.lng));
      // Universal maps link; Apple devices honor it, others open Google.
      return "https://www.google.com/maps/dir/?api=1&destination=" + dest;
    }
    function normUrl(u) {
      u = String(u || "").trim();
      if (!u) return "#";
      return /^https?:\/\//i.test(u) ? u : "https://" + u;
    }
    // Build the shareable deep link to one store and either invoke the native
    // share sheet (mobile) or copy to clipboard (desktop), with inline
    // "Copied!" feedback on the button.
    function storeDeepLink(id) {
      try { var u = new URL(window.location.href); u.searchParams.set("store", id); return u.toString(); }
      catch (e) { return window.location.href; }
    }
    function shareStore(s, btn) {
      var url = storeDeepLink(s.id);
      var span = btn.querySelector("span");
      var flash = function () {
        if (!span) return;
        var old = span.textContent;
        span.textContent = "Copied!";
        setTimeout(function () { span.textContent = old; }, 1600);
      };
      if (navigator.share) {
        navigator.share({ title: s.name, url: url }).catch(function () {});
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(flash).catch(function () {});
      } else {
        flash();
      }
    }

    // ---- map ----
    function showMapMsg(msg) {
      if (!msg) { mapMsg.hidden = true; return; }
      mapMsg.hidden = false;
      mapMsg.textContent = msg;
    }
    function initMap() {
      if (!cfg.token) {
        showMapMsg("Map unavailable — add a Mapbox token in the theme editor.");
        return;
      }
      whenMapbox(function (mapboxgl) {
        if (!mapboxgl) { showMapMsg("Map failed to load."); return; }
        try {
          mapboxgl.accessToken = cfg.token;
          var map = new mapboxgl.Map({
            container: mapEl,
            style: cfg.mapStyle,
            center: cfg.center,
            zoom: cfg.zoom,
            cooperativeGestures: true,
          });
          state.map = map;
          map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

          // A map created while its container has 0 height (common inside
          // theme sections that finish layout after the deferred script
          // runs) renders into a 0x0 canvas — tiles load (200s) but nothing
          // is visible. Force resizes once layout settles, and keep the map
          // sized to the container with a ResizeObserver.
          var kick = function () { try { map.resize(); } catch (e) {} };
          requestAnimationFrame(kick);
          setTimeout(kick, 250);
          setTimeout(kick, 800);
          if (window.ResizeObserver) {
            var ro = new ResizeObserver(kick);
            ro.observe(mapEl);
          }
          window.addEventListener("load", kick);

          map.on("load", function () {
            kick();
            map.addSource("ssp-stores", {
              type: "geojson",
              data: geojson(state.visible),
              cluster: true,
              clusterRadius: 46,
              clusterMaxZoom: 12,
            });
            map.addLayer({
              id: "ssp-clusters",
              type: "circle",
              source: "ssp-stores",
              filter: ["has", "point_count"],
              paint: {
                "circle-color": cfg.markerColor,
                "circle-opacity": 0.92,
                "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 25, 26],
                "circle-stroke-width": 2,
                "circle-stroke-color": "#FFFFFF",
              },
            });
            map.addLayer({
              id: "ssp-cluster-count",
              type: "symbol",
              source: "ssp-stores",
              filter: ["has", "point_count"],
              layout: {
                "text-field": ["get", "point_count_abbreviated"],
                "text-size": 13,
                "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
              },
              paint: { "text-color": "#1F1D18" },
            });
            map.addLayer({
              id: "ssp-points",
              type: "circle",
              source: "ssp-stores",
              filter: ["!", ["has", "point_count"]],
              paint: {
                "circle-color": cfg.markerColor,
                "circle-radius": 7,
                "circle-stroke-width": 2,
                "circle-stroke-color": "#FFFFFF",
              },
            });
            map.addLayer({
              id: "ssp-points-active",
              type: "circle",
              source: "ssp-stores",
              filter: ["==", ["get", "id"], -1],
              paint: {
                "circle-color": cfg.markerColor,
                "circle-radius": 11,
                "circle-stroke-width": 3,
                "circle-stroke-color": "#FFFFFF",
              },
            });

            map.on("click", "ssp-clusters", function (e) {
              var f = map.queryRenderedFeatures(e.point, { layers: ["ssp-clusters"] })[0];
              if (!f) return;
              map.getSource("ssp-stores").getClusterExpansionZoom(f.properties.cluster_id, function (err, z) {
                if (err) return;
                map.easeTo({ center: f.geometry.coordinates, zoom: z });
              });
            });
            map.on("click", "ssp-points", function (e) {
              var f = e.features && e.features[0];
              if (f) selectStore(f.properties.id, { fly: true, fromMap: true });
            });
            ["ssp-clusters", "ssp-points"].forEach(function (lid) {
              map.on("mouseenter", lid, function () { map.getCanvas().style.cursor = "pointer"; });
              map.on("mouseleave", lid, function () { map.getCanvas().style.cursor = ""; });
            });

            state.mapReady = true;
            fitToVisible();
            if (state.selectedId) selectStore(state.selectedId, { fly: true });
          });
          map.on("error", function (e) {
            var err = e && e.error;
            var status = err && (err.status || (err.response && err.response.status));
            var msg = (err && err.message) || "";
            if (status === 401 || status === 403 || /access token|401|403|unauthorized|forbidden/i.test(msg)) {
              showMapMsg("Map token rejected — check the Mapbox token (and its URL restrictions) in the theme editor.");
            }
            if (window.console) console.error("[ssp-locator] map error:", msg || err);
          });
        } catch (err) {
          showMapMsg("Map failed to initialize.");
          if (window.console) console.error("[ssp-locator]", err);
        }
      });
    }

    function refreshMapData() {
      if (!state.mapReady) return;
      var src = state.map.getSource("ssp-stores");
      if (src) src.setData(geojson(state.visible));
      fitToVisible();
    }

    function fitToVisible() {
      if (!state.mapReady) return;
      var pts = state.visible;
      if (!pts.length) return;
      if (state.origin) {
        // Keep the origin in view alongside results.
        var b = new window.mapboxgl.LngLatBounds();
        b.extend([state.origin.lng, state.origin.lat]);
        pts.slice(0, 25).forEach(function (s) { b.extend([s.lng, s.lat]); });
        state.map.fitBounds(b, { padding: 56, maxZoom: 12, duration: 500 });
      } else if (pts.length === 1) {
        state.map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 11 });
      } else {
        var bb = new window.mapboxgl.LngLatBounds();
        pts.forEach(function (s) { bb.extend([s.lng, s.lat]); });
        state.map.fitBounds(bb, { padding: 48, maxZoom: 11, duration: 500 });
      }
    }

    function highlightPoint(id) {
      if (!state.mapReady) return;
      state.map.setFilter("ssp-points-active", ["==", ["get", "id"], id == null ? -1 : id]);
    }

    // ---- selection ----
    function selectStore(id, opts) {
      opts = opts || {};
      var s = findStore(id);
      if (!s) return;
      state.selectedId = id;
      markActiveCard(id);
      highlightPoint(id);
      setDeepLink(id);
      if (!opts.fromMap) scrollCardIntoView(id);
      if (state.mapReady && opts.fly) {
        state.map.flyTo({ center: [s.lng, s.lat], zoom: Math.max(state.map.getZoom(), 11), duration: 600 });
      }
      openPopup(s);
    }
    function findStore(id) {
      for (var i = 0; i < state.all.length; i++) { if (String(state.all[i].id) === String(id)) return state.all[i]; }
      return null;
    }
    function markActiveCard(id) {
      Object.keys(state.cardById).forEach(function (k) {
        state.cardById[k].classList.toggle("is-active", String(k) === String(id));
      });
    }
    function scrollCardIntoView(id) {
      var card = state.cardById[id];
      if (card && card.scrollIntoView) card.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    function openPopup(s) {
      if (!state.mapReady) return;
      if (state.popup) state.popup.remove();
      var lines = addrLines(s.address).map(esc).join("<br>");
      var html =
        '<div class="ssp-pop">' +
        '<p class="ssp-pop__name">' + esc(s.name) + "</p>" +
        (lines ? '<p class="ssp-pop__addr">' + lines + "</p>" : "") +
        '<a class="ssp-pop__link" href="' + esc(directionsUrl(s)) + '" target="_blank" rel="noopener">Get directions →</a>' +
        "</div>";
      state.popup = new window.mapboxgl.Popup({ offset: 14, closeButton: true, maxWidth: "240px" })
        .setLngLat([s.lng, s.lat])
        .setHTML(html)
        .addTo(state.map);
    }

    // ---- deep linking (?store=ID) ----
    function setDeepLink(id) {
      if (!window.history || !window.history.replaceState) return;
      try {
        var u = new URL(window.location.href);
        u.searchParams.set("store", id);
        window.history.replaceState({}, "", u);
      } catch (e) {}
    }
    function applyDeepLink() {
      try {
        var u = new URL(window.location.href);
        var id = u.searchParams.get("store");
        if (id && findStore(id)) {
          state.selectedId = id;
          markActiveCard(id);
          if (state.mapReady) selectStore(id, { fly: true });
        }
      } catch (e) {}
    }

    // ---- search + near me ----
    function geocode(query) {
      if (!cfg.token) return Promise.reject(new Error("no token"));
      var url = "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
        encodeURIComponent(query) + ".json?access_token=" + encodeURIComponent(cfg.token) +
        "&limit=1&autocomplete=false&types=postcode,place,locality,neighborhood,address" +
        (cfg.country ? "&country=" + encodeURIComponent(cfg.country) : "");
      return fetch(url).then(function (r) { return r.json(); }).then(function (d) {
        var f = d && d.features && d.features[0];
        if (!f) throw new Error("no match");
        return { lng: f.center[0], lat: f.center[1] };
      });
    }
    function setOrigin(origin) {
      state.origin = origin;
      state.radius = parseInt(radiusSel.value, 10) || 25;
      radiusWrap.hidden = false;
      applyFilters();
      refreshMapData();
    }
    function clearOrigin() {
      state.origin = null;
      radiusWrap.hidden = true;
      applyFilters();
      refreshMapData();
    }

    if (emptyResetBtn) emptyResetBtn.addEventListener("click", function () {
      state.channels = {};
      state.lines = {};
      if (channelSelectEl) channelSelectEl.value = "";
      syncLineChips();
      searchInput.value = "";
      clearBtn.hidden = true;
      clearOrigin(); // resets origin + radius, then re-applies filters + map
    });

    searchForm.addEventListener("submit", function (e) { e.preventDefault(); runSearch(); });
    searchInput.addEventListener("input", function () {
      clearBtn.hidden = !searchInput.value;
    });
    clearBtn.addEventListener("click", function () {
      searchInput.value = "";
      clearBtn.hidden = true;
      clearOrigin();
      searchInput.focus();
    });
    radiusSel.addEventListener("change", function () {
      state.radius = parseInt(radiusSel.value, 10) || 0;
      applyFilters();
      refreshMapData();
    });
    nearBtn.addEventListener("click", function () {
      if (!navigator.geolocation) { return; }
      nearBtn.setAttribute("aria-busy", "true");
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          nearBtn.removeAttribute("aria-busy");
          searchInput.value = "";
          clearBtn.hidden = true;
          setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        function () {
          nearBtn.removeAttribute("aria-busy");
          showMapMsgTransient("Couldn't get your location. Try searching a ZIP or city.");
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    });

    function runSearch() {
      var q = searchInput.value.trim();
      if (!q) { clearOrigin(); return; }
      geocode(q)
        .then(function (origin) { setOrigin(origin); })
        .catch(function () { showMapMsgTransient('No location found for "' + q + '".'); });
    }
    var msgTimer = null;
    function showMapMsgTransient(msg) {
      showMapMsg(msg);
      if (msgTimer) clearTimeout(msgTimer);
      msgTimer = setTimeout(function () { if (state.mapReady) showMapMsg(null); }, 3200);
    }
  }

  // ---- inline icons ----
  function pinSvg() { return '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="10" r="2.4" fill="currentColor"/></svg>'; }
  function phoneSvg() { return '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.6a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.5-1.3a2 2 0 0 1 2.1-.4c.8.3 1.7.6 2.6.7a2 2 0 0 1 1.7 2z" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>'; }
  function webSvg() { return '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>'; }
  function shareSvg() { return '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><circle cx="18" cy="5" r="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="6" cy="12" r="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="18" cy="19" r="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8.3 10.8 15.7 6.4M8.3 13.2l7.4 4.4" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>'; }

  function boot() {
    var roots = document.querySelectorAll("[data-ssp-locator]");
    Array.prototype.forEach.call(roots, function (r) {
      if (r.__sspInit) return;
      r.__sspInit = true;
      Locator(r);
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // Re-init when added via the Shopify theme editor.
  document.addEventListener("shopify:section:load", boot);
})();
